import React, { useState, useEffect, useMemo } from 'react';
import { Modal, Form, Button } from 'react-bootstrap';
import { FaSpinner } from 'react-icons/fa';
import Select from 'react-select';
import { toast } from 'react-toastify';
import { pledgeApi } from '../../api/pledges';
import { fundApi } from '../../api/funds';
import { guestApi } from '../../api/guestList';
import { useAuthContext } from '../../context/AuthContext';
import { handleApiResponse } from '../global/ApiMessageModal/ApiMessageModal';

const OngezaMchangoModal = ({ show, onHide, onSuccess }) => {
  const { user, isAuthenticated } = useAuthContext();
  const [guests, setGuests] = useState([]);
  const [loadingGuests, setLoadingGuests] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [selectedGuestOption, setSelectedGuestOption] = useState(null);
  const [selectedGuest, setSelectedGuest] = useState(null);
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [receivedBy, setReceivedBy] = useState('');

  const formatCurrencyInput = (val) => {
    if (val === null || val === undefined || val === '') return '';
    const digitsOnly = String(val).replace(/[^\d]/g, '');
    if (!digitsOnly) return '';
    return parseInt(digitsOnly, 10).toLocaleString('en-US');
  };

  const parseCurrencyValue = (val) => {
    if (!val && val !== 0) return 0;
    const digitsOnly = String(val).replace(/[^\d]/g, '');
    return parseFloat(digitsOnly) || 0;
  };

  const guestOptions = useMemo(() => {
    if (!guests || !Array.isArray(guests)) return [];
    return guests.map(g => ({
      value: g.id,
      label: `${g.name}${g.pledge > 0 ? ` - Ahadi: TZS ${g.pledge.toLocaleString()}` : ''}${g.fund > 0 ? ` (Mchango: TZS ${g.fund.toLocaleString()})` : ''}`
    }));
  }, [guests]);

  useEffect(() => {
    if (show) {
      loadGuests();
      setSelectedGuestOption(null);
      setSelectedGuest(null);
      setAmount('');
      setDescription('');
      setReceivedBy(user?.full_name || 'Admin');
    }
  }, [show, user]);

  const loadGuests = async () => {
    setLoadingGuests(true);
    try {
      const res = await fundApi.getFunds();
      const resData = res?.data;

      let rawData = [];
      if (Array.isArray(resData)) {
        rawData = resData;
      } else if (resData?.data) {
        if (Array.isArray(resData.data.data)) {
          rawData = resData.data.data;
        } else if (Array.isArray(resData.data)) {
          rawData = resData.data;
        }
      }

      const guestsMap = new Map();

      rawData.forEach(item => {
        const guestObj = item.pledge?.guest || item.guest || {};
        const guestId = guestObj.id || item.guest_id || item.pledge?.guest_id || item.id;
        const guestName = guestObj.name || item.pledger || item.full_name || item.name || 'Mgeni';
        const phone = guestObj.phone_number || item.phone_number || item.mobile || item.phone || '';
        const email = guestObj.email || item.email || null;
        const groups = guestObj.groups || item.groups || [];

        const pledgeAmt = parseFloat(item.pledge?.amount || item.pledge_amount || item.pledge || 0);
        const fundAmt = parseFloat(item.amount || item.fund || item.paid || 0);

        if (guestsMap.has(guestId)) {
          const existing = guestsMap.get(guestId);
          existing.fund += fundAmt;
          if (pledgeAmt > existing.pledge) existing.pledge = pledgeAmt;
          if (!existing.phone_number && phone) existing.phone_number = phone;
          if (!existing.email && email) existing.email = email;
          if ((!existing.groups || existing.groups.length === 0) && groups.length > 0) existing.groups = groups;
        } else {
          guestsMap.set(guestId, {
            id: guestId,
            pledge_id: item.pledge_id || item.pledge?.id || item.id,
            name: guestName,
            phone_number: phone,
            email: email,
            pledge: pledgeAmt,
            fund: fundAmt,
            groups: groups
          });
        }
      });

      setGuests(Array.from(guestsMap.values()));
    } catch (err) {
      console.error('Failed to load funds list from /api/funds:', err);
      setGuests([]);
    } finally {
      setLoadingGuests(false);
    }
  };

  const handleSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();

    if (!selectedGuestOption || !selectedGuestOption.value) {
      toast.warning('Tafadhali chagua mchangiaji / mgeni');
      return;
    }

    const numericAmount = parseCurrencyValue(amount);
    if (!numericAmount || numericAmount <= 0) {
      toast.warning('Tafadhali ingiza kiasi sahihi cha mchango');
      return;
    }

    const targetGuest = selectedGuest || guests.find(g => String(g.id) === String(selectedGuestOption.value) || String(g.pledge_id) === String(selectedGuestOption.value));
    if (!targetGuest) {
      toast.error('Mgeni aliyechaguliwa hajapatikana');
      return;
    }

    setSubmitting(true);
    try {
      const groupIds = (targetGuest.groups || []).map(g => {
        if (typeof g === 'number' || typeof g === 'string') return g;
        if (typeof g === 'object' && g !== null) {
          return g.pivot?.guest_list_group_id || g.pivot?.group_id || g.guest_list_group_id || g.group_id || g.id || g.value || null;
        }
        return null;
      }).filter(id => id !== null && id !== undefined);

      const firstGroupId = groupIds.length > 0 ? groupIds[0] : null;

      const payload = {
        name: String(targetGuest.name).trim(),
        phone_number: targetGuest.phone_number || '',
        email: targetGuest.email || null,
        pledge: parseFloat(targetGuest.pledge) || 0,
        fund: numericAmount, // Contribution amount to update fund (same method as EditGuestModal)
        groups: groupIds,
        group_ids: groupIds,
        group_id: firstGroupId,
        guest_list_group_id: firstGroupId
      };

      const response = await guestApi.updateGuest(targetGuest.id, payload);

      if (handleApiResponse(response)) {
        toast.success(`Mchango wa TZS ${numericAmount.toLocaleString()} umerekodiwa kikamilifu!`);
        onHide();
        if (onSuccess) onSuccess();
      }
    } catch (err) {
      console.error('Error updating guest fund:', err);
      handleApiResponse(err);
    } finally {
      setSubmitting(false);
    }
  };

  if (isAuthenticated === false) return null;

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Rekodi Mchango Mpya</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form>
          <div className="row">
            <div className="col-md-6">
              <Form.Group className="mb-3">
                <Form.Label>Mchangiaji / Mgeni *</Form.Label>
                {loadingGuests ? (
                  <div className="d-flex align-items-center gap-2 py-2 text-muted small">
                    <FaSpinner className="fa-spin" /> Inapakia orodha ya wageni...
                  </div>
                ) : (
                  <Select
                    options={guestOptions}
                    value={selectedGuestOption}
                    onChange={(selected) => {
                      setSelectedGuestOption(selected);
                      if (selected) {
                        const g = guests.find(item => item.id === selected.value);
                        setSelectedGuest(g || null);
                      } else {
                        setSelectedGuest(null);
                      }
                    }}
                    placeholder="-- Chagua Mchangiaji / Mgeni --"
                    className="react-select-container"
                    classNamePrefix="react-select"
                    isClearable
                  />
                )}
              </Form.Group>
            </div>
            <div className="col-md-6">
              <Form.Group className="mb-3">
                <Form.Label>Mchango (TZS) *</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="e.g., 50,000"
                  value={amount}
                  onChange={(e) => setAmount(formatCurrencyInput(e.target.value))}
                />
                {amount && (
                  <Form.Text className="text-success fw-bold d-block mt-1" style={{ fontSize: '0.825rem' }}>
                    Kiasi Kipya: TZS {formatCurrencyInput(amount)}
                  </Form.Text>
                )}
                {selectedGuest && (
                  <Form.Text className="text-muted d-block mt-1" style={{ fontSize: '0.8rem' }}>
                    Jumla ya Sasa: TZS {(selectedGuest.fund || 0).toLocaleString()} | Ahadi: TZS {(selectedGuest.pledge || 0).toLocaleString()}
                  </Form.Text>
                )}
              </Form.Group>
            </div>
          </div>

          <div className="row">
            <div className="col-md-6">
              <Form.Group className="mb-3">
                <Form.Label>Mhifadhi / Aliyepokea</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Jina la aliyepokea mchango"
                  value={receivedBy}
                  onChange={(e) => setReceivedBy(e.target.value)}
                />
              </Form.Group>
            </div>
            <div className="col-md-6">
              <Form.Group className="mb-3">
                <Form.Label>Maelezo (Si Lazima)</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Maelezo ya ziada kuhusu mchango"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
              </Form.Group>
            </div>
          </div>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Funga
        </Button>
        <Button variant="primary" onClick={handleSubmit} disabled={submitting}>
          {submitting ? <FaSpinner className="fa-spin" /> : 'Hifadhi Mchango'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default OngezaMchangoModal;

